/**
 * NonCanonicalPill — P0-B Sub-Step 2a
 *
 * Renders a warning pill for each legacy value stored in
 * `user_preferences` that could NOT be auto-migrated to a canonical
 * taxonomy entry (confidence < 0.8). The user can remove the value via
 * the X-button. The Batch-5b-2 follow-up will add a one-click
 * "Canonical setzen"-CTA via Resolver API.
 *
 * Visual: rose-tinted chip with the legacy text, optionally a tooltip
 * showing the resolver's best-guess match below threshold.
 */
import * as React from "react";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * @param {{
 *   field: string,                  // human-readable field name, e.g. "Modell"
 *   values: any[],                  // raw legacy values
 *   suggestions?: Record<string, string>,  // raw → best-guess match (≥0.5, <0.8)
 *   onRemove: (raw: any) => void,
 *   dataTestId?: string,
 * }} props
 */
export function NonCanonicalPillList({
  field,
  values,
  suggestions = {},
  onRemove,
  dataTestId,
}) {
  const { t } = useTranslation("preferences");
  if (!Array.isArray(values) || values.length === 0) return null;
  return (
    <div
      data-testid={dataTestId || "non-canonical-pill-list"}
      className="flex flex-wrap gap-1.5 mt-2"
      role="alert"
      aria-label={t("nonCanonical.ariaList", { field })}
    >
      {values.map((v, i) => {
        const suggestion = suggestions?.[v];
        return (
          <span
            key={`${v}-${i}`}
            title={
              suggestion
                ? t("nonCanonical.bestGuess", { suggestion })
                : t("nonCanonical.noMatch")
            }
            data-testid={`${dataTestId || "noncanonical"}-${i}`}
            className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-xs text-rose-200"
          >
            <AlertTriangle className="h-3 w-3" />
            <span>{t("nonCanonical.label", { value: String(v) })}</span>
            <button
              type="button"
              aria-label={t("nonCanonical.remove", { value: v })}
              data-testid={`${dataTestId || "noncanonical"}-${i}-remove`}
              onClick={() => onRemove(v)}
              className="ml-0.5 rounded-sm text-rose-300 hover:text-rose-100 focus:outline-none focus:ring-1 focus:ring-rose-300"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      })}
    </div>
  );
}

export default NonCanonicalPillList;
